import { AlertCircle, RefreshCw, Search } from "lucide-react";

import { useEffect, useState } from "react";

import { useAddSourceURL } from "@/api/knowledge-source/add-source-url";
import { useGetTrainingHistory } from "@/api/knowledge-source/get-training-history";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import PaginationCombo from "@/components/ui/pagination-combo";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import useUpdateQueryParams from "@/hooks/use-update-query-params";
import { useSearchParams } from "react-router";
import { cn } from "@/lib/utils";
import useGlobalPermission from "@/store/user-permissions";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

type SourceType = {
    id: string;
    url: string;
    success: boolean;
    character_size: number;
};

const TrainingHistoryTable = () => {
    const updateQueryParams = useUpdateQueryParams();
    const [searchParams] = useSearchParams();

    const { permissionList } = useGlobalPermission();
    const trainingPermission = permissionList.find((item) => item.module.name === "AI_Training");

    const { data, isLoading, isError } = useGetTrainingHistory({
        skip: (Number(searchParams.get("page") || "1") - 1) * Number(searchParams.get("per_page") || "10"),
        limit: Number(searchParams.get("per_page") || "10"),
        sourceType: searchParams.get("sourceType") || "website",
        status: searchParams.get("status") || undefined,
    });

    const { mutate: refetchSource, isPending: isRefetching } = useAddSourceURL();
    const [refetchingId, setRefetchingId] = useState<string | null>(null);

    const handleRefetch = (url: string, id: string) => {
        if (!trainingPermission?.can_create) return;
        setRefetchingId(id);
        refetchSource(
            { url },
            {
                onSuccess: () => {
                    setRefetchingId(null);
                },
                onError: () => {
                    setRefetchingId(null);
                },
            }
        );
    };

    // Set default sourceType to "website" if not present in URL params on initial load
    useEffect(() => {
        if (!searchParams.get("sourceType")) {
            updateQueryParams({ sourceType: "website" });
        }
    }, []); // Only run on initial mount

    return (
        <div className="relative">
            {!trainingPermission?.can_get_list && (
                <Alert variant="destructive" className="absolute z-10 top-1/2 right-1/2 translate-x-1/2 -translate-y-1/2 max-w-2xl">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>You do not have permission to view this list!</AlertDescription>
                </Alert>
            )}
            <Card className={cn("mt-4 p-4 text-gray-700 shadow-none", !trainingPermission?.can_get_list && "blur-md")}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Search />
                        <div>
                            <h4 className="text-lg font-semibold">Training History ({data?.pagination?.total_entity || 0})</h4>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Select
                            onValueChange={(value) => {
                                if (value === "all") {
                                    // When "all" is selected, remove the status parameter from URL
                                    const { status, ...rest } = Object.fromEntries(searchParams.entries());
                                    updateQueryParams({ ...rest, page: "1" });
                                } else {
                                    // Convert UI values to API values
                                    const apiValue = value === "success" ? "true" : "false";
                                    updateQueryParams({ status: apiValue, page: "1" });
                                }
                            }}
                            value={searchParams.get("status") === "true" ? "success" : searchParams.get("status") === "false" ? "failed" : "all"}
                        >
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <SelectLabel>Status</SelectLabel>
                                    <SelectItem value="all">All Status</SelectItem>
                                    <SelectItem value="success">Success</SelectItem>
                                    <SelectItem value="failed">Failed</SelectItem>
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                        <Select
                            onValueChange={(value) => {
                                if (value === "website") {
                                    // When "website" is selected (default), remove the sourceType parameter from URL
                                    const { sourceType, ...rest } = Object.fromEntries(searchParams.entries());
                                    updateQueryParams({ ...rest, page: "1" });
                                } else {
                                    updateQueryParams({ sourceType: value, page: "1" });
                                }
                            }}
                            value={searchParams.get("sourceType") || "website"}
                        >
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Select a source" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <SelectLabel>Source</SelectLabel>
                                    <SelectItem value="website">Website</SelectItem>
                                    <SelectItem value="file">File</SelectItem>
                                    <SelectItem value="custom_text">Text</SelectItem>
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                        {(searchParams.get("status") || searchParams.get("sourceType")) && (
                            <Button
                                onClick={() => {
                                    // Clear both filters by removing them (this will default back to status "all" and sourceType "website")
                                    updateQueryParams({ page: "1", per_page: "10" });
                                }}
                            >
                                Clear
                            </Button>
                        )}
                    </div>
                </div>
                <Separator className="my-4" />
                <div className="mt-4 max-h-96 overflow-y-auto">
                    <Table>
                        <TableHeader className="sticky top-0 bg-white">
                            <TableRow>
                                <TableHead>
                                    {searchParams.get("sourceType") === "website" && "URL"}
                                    {searchParams.get("sourceType") === "file" && "Filename"}
                                    {searchParams.get("sourceType") === "custom_text" && "Text"}
                                </TableHead>
                                <TableHead>Character Size</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Action</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading &&
                                [...Array(10)].map((_, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Skeleton className="h-4 w-[250px]" />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton className="h-4 w-[250px]" />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton className="h-4 w-[250px]" />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton className="h-4 w-[100px]" />
                                        </TableCell>
                                    </TableRow>
                                ))}

                            {!data?.data && (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <div className="flex flex-col items-center justify-between">
                                            <img src={"/assets/images/illustrations/empty.svg"} height={200} width={200} alt="no process" />
                                            <h3 className="my-4 text-lg font-semibold text-slate-700">No Data</h3>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                            {!isLoading &&
                                !isError &&
                                data?.data &&
                                data.data.map((src: SourceType) => (
                                    <TableRow key={src.id}>
                                        <TableCell className="font-semibold text-green-600">
                                            {!trainingPermission?.can_get_list ? "No permission to view" : src?.url}
                                        </TableCell>
                                        <TableCell className="font-semibold text-green-600">
                                            {!trainingPermission?.can_get_list ? "No permission to view" : src?.character_size}
                                        </TableCell>
                                        <TableCell className={cn("font-semibold ", src?.success ? "text-green-600" : "text-red-600")}>
                                            {!trainingPermission?.can_get_list ? "No permission to view" : src?.success ? "Success" : "Failed"}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleRefetch(src.url, src.id)}
                                                disabled={isRefetching || refetchingId === src.id || !trainingPermission?.can_create}
                                                className="flex items-center gap-2"
                                            >
                                                {(isRefetching || refetchingId === src.id) && <RefreshCw className="h-4 w-4 animate-spin" />}
                                                {!isRefetching && refetchingId !== src.id && <RefreshCw className="h-4 w-4" />}
                                                Refetch
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </div>
                {data?.data && <PaginationCombo totalItems={data?.pagination?.total_entity || 0} />}
            </Card>
        </div>
    );
};

export default TrainingHistoryTable;
